import type { LineReportItem, ProductionReferenceData, ProductionRun } from "../models";
import { aggregateQuantityGroups, singleQuantityGroup, sumKnownQuantities } from "../../../../shared/production/quantity";
import { completedDowntimeRequiresReason, isShortProductionDowntime } from "../../../../shared/production/downtime";
import { operatorDowntimeGroups } from "../operator-report-summary";
import { netProductionMinutes, productionProjection, rateMetrics } from "../production-metrics";
import { amount } from "../operator/quantity-format";
import { shiftEndTimestamp } from "../production-time";
import { runTiming } from "./run-timing";

export type SupervisorLineFilter = "all" | "active" | "ready" | "stopped";
export type ManagerReportView = "overview" | "plan" | "losses" | "operators" | "data-quality";

export function supervisorBoardModel(
  shiftRuns: ProductionRun[],
  shiftReport: LineReportItem[],
  reference: ProductionReferenceData | null,
  operationalDate: string,
  activeShiftCode: string,
  lineFilter: SupervisorLineFilter,
  now: number,
) {
  const shiftLiveRuns = shiftRuns.filter((run) => run.status !== "COMPLETED");
  const shiftStoppedCount = shiftLiveRuns.filter((run) =>
    run.downtimes.some((item) => !item.endedAt),
  ).length;
  const shiftActiveCount = shiftLiveRuns.filter(
    (run) =>
      run.status === "ACTIVE" && !run.downtimes.some((item) => !item.endedAt),
  ).length;
  const shiftReadyCount = shiftLiveRuns.filter(
    (run) => run.status === "READY",
  ).length;
  const summaryGroups = aggregateQuantityGroups(shiftRuns);
  const summaryTotals = singleQuantityGroup(summaryGroups);
  const summaryUnit = summaryTotals.unit;
  const summaryProduction = summaryTotals.production;
  const summaryPlanned = summaryUnit && shiftRuns.every((run) => run.plannedQuantity != null)
    ? sumKnownQuantities(shiftRuns.map((run) => run.plannedQuantity)) : null;
  const summaryRemaining = summaryPlanned == null || summaryProduction == null ? null : Math.max(0, summaryPlanned - summaryProduction);
  const summaryAttainment = summaryPlanned && summaryPlanned > 0 && summaryProduction != null ? Math.min(100, summaryProduction / summaryPlanned * 100) : null;
  const summaryScrap = sumKnownQuantities([summaryTotals.waste, summaryTotals.scrap]);
  const summaryDowntime = shiftReport.reduce(
    (sum, row) => sum + row.downtimeMinutes,
    0,
  );
  const pendingCodingCount = shiftRuns
    .flatMap((run) => run.entries)
    .filter(
      (entry) =>
        !entry.voidedAt &&
        entry.type === "SCRAP" &&
        entry.erpCodingStatus === "PENDING_CODING",
    ).length;
  const filteredShiftReport = shiftReport.filter((row) => {
    if (lineFilter === "all") return true;
    const lineRuns = shiftRuns.filter((run) => run.lineId === row.lineId);
    if (lineFilter === "stopped")
      return lineRuns.some(
        (run) =>
          run.status === "ACTIVE" &&
          run.downtimes.some((item) => !item.endedAt),
      );
    if (lineFilter === "active")
      return lineRuns.some(
        (run) =>
          run.status === "ACTIVE" &&
          !run.downtimes.some((item) => !item.endedAt),
      );
    return lineRuns.some((run) => run.status === "READY");
  });
  const remainingShiftMinutes = Math.max(0, (shiftEndTimestamp(operationalDate, activeShiftCode) - now) / 60_000);
  const managerLineMetrics = shiftReport
    .map((row) => {
      const lineRuns = shiftRuns.filter((run) => run.lineId === row.lineId);
      const speed = rateMetrics(lineRuns, reference, now);
      const netMinutes = lineRuns.reduce(
        (sum, run) => sum + netProductionMinutes(run, now),
        0,
      );
      const totalMinutes = netMinutes + row.downtimeMinutes;
      const availability = totalMinutes > 0 ? (netMinutes / totalMinutes) * 100 : null;
      const loss = sumKnownQuantities([row.waste, row.scrap]);
      const totalOutput = sumKnownQuantities([row.production, loss]);
      const scrapRate = totalOutput != null && totalOutput > 0 && loss != null ? loss / totalOutput * 100 : null;
      const projection = productionProjection(lineRuns, reference, remainingShiftMinutes, now);
      const planned = projection.plannedQuantity;
      const attainment = planned != null && planned > 0 && row.production != null ? row.production / planned * 100 : null;
      const projectedProduction = projection.projectedQuantity ?? row.production;
      const projectedAttainment = projection.attainment;
      const stopped = lineRuns.some(
        (run) => run.status === "ACTIVE" && run.downtimes.some((item) => !item.endedAt),
      );
      const active = lineRuns.some((run) => run.status === "ACTIVE");
      const quantityComplete = active && planned != null && planned > 0 && row.production != null && row.production >= planned - 0.000_001;
      const currentRun =
        lineRuns.find((run) => run.status === "ACTIVE" && run.downtimes.some((item) => !item.endedAt)) ??
        lineRuns.find((run) => run.status === "ACTIVE") ??
        lineRuns.find((run) => run.status === "READY") ??
        lineRuns[0] ?? null;
      const timing = currentRun ? runTiming(currentRun, reference, now) : null;
      const pendingReason = lineRuns.some((run) =>
        run.downtimes.some((item) => completedDowntimeRequiresReason(item) && !item.reasonCodeId && !item.reasonName),
      );
      const hasProductionRecord = row.production != null && row.production > 0;
      const hasPlannedSpeed = speed.plannedRate != null;
      const hasMeasuredSpeed = speed.actualRate != null && hasProductionRecord;
      const speedState = !active
        ? "inactive"
        : stopped
          ? "stopped"
          : quantityComplete
            ? "complete"
            : !hasPlannedSpeed
              ? "missing-target"
              : !hasMeasuredSpeed
                ? "awaiting-production"
                : speed.performance != null && speed.performance < 85
                  ? "below-target"
                  : "on-target";
      const severity = stopped
        ? 4
        : pendingReason
          ? 3
          : speedState === "below-target"
            ? 2
            : scrapRate != null && scrapRate > 3
              ? 2
              : 0;
      const signal = stopped
        ? "Duruşa müdahale et"
        : pendingReason
          ? "Duruş nedeni bekliyor"
          : quantityComplete
            ? "Miktar tamamlandı · kapanış bekleniyor"
            : speedState === "below-target"
              ? "Hız hedefin altında"
              : scrapRate != null && scrapRate > 3
                ? "Iskarta oranını incele"
                : active
                  ? "Hat normal çalışıyor"
                  : "Aktif üretim yok";
      const largestDowntime = lineRuns
        .flatMap((run) => run.downtimes)
        .map((item) => ({
          name: isShortProductionDowntime(item) ? "Kısa duruşlar" : item.reasonName ?? "Nedeni girilmemiş duruş",
          minutes: Math.max(0, ((item.endedAt ? new Date(item.endedAt).getTime() : now) - new Date(item.startedAt).getTime()) / 60_000),
        }))
        .sort((left, right) => right.minutes - left.minutes)[0];
      const primaryLoss = stopped
        ? `${largestDowntime?.name ?? "Duruş"} · ${Math.round(largestDowntime?.minutes ?? row.downtimeMinutes)} dk`
        : scrapRate != null && scrapRate > 3
          ? `Iskarta · %${scrapRate.toFixed(1).replace(".", ",")}`
          : speedState === "below-target"
            ? `Hız performansı · %${Math.round(speed.performance ?? 0)}`
            : speedState === "awaiting-production"
              ? "Üretim miktarı bekleniyor"
              : speedState === "missing-target"
                ? "Hız hedefi tanımlı değil"
                : "—";
      const performanceLabel = speedState === "inactive"
        ? "Aktif üretim yok"
        : speedState === "stopped"
          ? "Duruş nedeniyle ölçüm askıda"
          : speedState === "complete"
            ? "Plan miktarı tamamlandı"
            : speedState === "missing-target"
              ? "Planlanan hız tanımlı değil"
              : speedState === "awaiting-production"
                ? "İlk üretim kaydı bekleniyor"
                : speedState === "below-target"
                  ? `Ölçülen hız hedefin %${Math.round(100 - (speed.performance ?? 0))} altında`
                  : "Ölçülen hız hedefe uygun";
      return { ...row, planned, projectedProduction, projectedAttainment, speed, speedState, performanceLabel, availability, scrapRate, attainment, stopped, active, quantityComplete, severity, signal, primaryLoss, currentRun, timing };
    })
    .sort((left, right) => right.severity - left.severity || left.lineName.localeCompare(right.lineName, "tr-TR"));
  const managerNetMinutes = shiftRuns.reduce(
    (sum, run) => sum + netProductionMinutes(run, now),
    0,
  );
  const managerAvailability = managerNetMinutes + summaryDowntime > 0
    ? (managerNetMinutes / (managerNetMinutes + summaryDowntime)) * 100
    : null;
  const managerScrapRate = summaryProduction != null && summaryScrap != null && summaryProduction + summaryScrap > 0
    ? (summaryScrap / (summaryProduction + summaryScrap)) * 100
    : null;
  const managementFocus = managerLineMetrics
    .filter((line) => line.severity > 0 || (line.projectedAttainment != null && line.projectedAttainment < 95))
    .map((line) => {
      const expectedShortfall = line.planned == null || line.projectedProduction == null ? null : Math.max(0, line.planned - line.projectedProduction);
      const consequence = expectedShortfall == null ? "Miktar tahmini için birimler doğrulanmalı" : expectedShortfall > 0
        ? `Vardiya sonunda yaklaşık ${amount(expectedShortfall)} eksik`
        : line.stopped
          ? `${line.downtimeMinutes} dk kapasite kaybı oluştu`
          : "Hedef hâlâ yakalanabilir";
      const status = line.stopped ? "Kritik" : line.projectedAttainment != null && line.projectedAttainment < 95 ? "Riskli" : "İzleniyor";
      return { ...line, expectedShortfall, consequence, status };
    })
    .sort((left, right) => right.severity - left.severity || (right.expectedShortfall ?? 0) - (left.expectedShortfall ?? 0))
    .slice(0, 3);
  const lossReasons = operatorDowntimeGroups(shiftRuns, now).map((item) => ({ ...item, minutes: item.milliseconds / 60_000 }));
  const largestLossMinutes = Math.max(1, ...lossReasons.map((item) => item.minutes));
  const dataQualityIssues = [
    {
      label: "Duruş nedeni eksik",
      count: shiftRuns.flatMap((run) => run.downtimes).filter((item) => completedDowntimeRequiresReason(item) && !item.reasonCodeId && !item.reasonName).length,
      detail: "Biten duruş kayıtlarında neden seçilmemiş.",
    },
    {
      label: "Iskarta kodlaması bekliyor",
      count: pendingCodingCount,
      detail: "ERP kodu henüz belirlenmemiş ıskarta kayıtları.",
    },
    {
      label: "Hız hedefi hesaplanamıyor",
      count: managerLineMetrics.filter((line) => line.active && line.speedState === "missing-target").length,
      detail: "Plan süresi veya ürün-adet dönüşümü eksik.",
    },
    {
      label: "Kapanış bekleyen iş",
      count: managerLineMetrics.filter((line) => line.quantityComplete).length,
      detail: "Plan miktarı tamamlanmış ancak iş kapatılmamış.",
    },
  ].filter((item) => item.count > 0);
  return { shiftLiveRuns, shiftStoppedCount, shiftActiveCount, shiftReadyCount, summaryUnit, summaryProduction, summaryPlanned, summaryRemaining, summaryAttainment, summaryScrap, summaryDowntime, pendingCodingCount, filteredShiftReport, managerLineMetrics, managerAvailability, managerScrapRate, managementFocus, lossReasons, largestLossMinutes, dataQualityIssues };
}

export type SupervisorBoardModel = ReturnType<typeof supervisorBoardModel>;

export function supervisorSummaryLine(row: LineReportItem, shiftRuns: ProductionRun[], reference: ProductionReferenceData | null, now: number) {
  const lineRuns = shiftRuns.filter(
    (run) => run.lineId === row.lineId,
  );
  const activeRuns = lineRuns.filter(
    (run) => run.status === "ACTIVE",
  );
  const stoppedRuns = activeRuns.filter((run) =>
    run.downtimes.some((item) => !item.endedAt),
  );
  const runningRuns = activeRuns.filter(
    (run) => !run.downtimes.some((item) => !item.endedAt),
  );
  const readyRuns = lineRuns.filter(
    (run) => run.status === "READY",
  );
  const completedRuns = lineRuns.filter(
    (run) => run.status === "COMPLETED",
  );
  const currentRun =
    stoppedRuns[0] ??
    runningRuns[0] ??
    readyRuns[0] ??
    [...completedRuns].sort(
      (left, right) =>
        new Date(right.closedAt ?? right.openedAt).getTime() -
        new Date(left.closedAt ?? left.openedAt).getTime(),
    )[0];
  const lineStatusText = stoppedRuns.length
    ? "Duruşta"
    : runningRuns.length
      ? "Üretimde"
      : readyRuns.length
        ? "Başlamaya hazır"
        : completedRuns.length
          ? "Tamamlandı"
          : "Bugün iş kaydı yok";
  const lineState = stoppedRuns.length
    ? "stopped"
    : runningRuns.length
      ? "active"
      : readyRuns.length
        ? "ready"
        : "completed";
  const planned = lineRuns.reduce(
    (sum, run) => sum + (run.plannedQuantity ?? 0),
    0,
  );
  const progress =
    planned > 0 && row.production != null
      ? Math.min(100, (row.production / planned) * 100)
      : 0;
  const linePending = lineRuns
    .flatMap((run) => run.entries)
    .filter(
      (entry) =>
        !entry.voidedAt &&
        entry.type === "SCRAP" &&
        entry.erpCodingStatus === "PENDING_CODING",
    ).length;
  const lineRate = rateMetrics(lineRuns, reference, now);
  return { row, currentRun, lineStatusText, lineState, planned, progress, linePending, lineRate };
}
